import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  navigationButtons,
  navigationGroups,
} from '../../lists/navigation-buttons-list';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LayoutService } from '../../services/layout.service';
import { NavigationButton } from '../models/NavigationButton';
import { TokenService } from 'src/app/shared/services/token.service';
import { environment } from 'src/environments/environment';
import {
  CompanyV2Dto,
  TransactionControllerService,
} from 'src/app/api-client';
import { UnreadTransactionsService } from 'src/app/shared/services/unread-transactions.service';
import { Subscription, filter } from 'rxjs';

const MOBILE_BREAKPOINT = 992;

@Component({
  selector: 'app-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.scss'],
})
export class BaseComponent implements OnInit, OnDestroy {
  private _layout = inject(LayoutService);

  private _token = inject(TokenService);

  private _transactions = inject(TransactionControllerService);

  private _route = inject(ActivatedRoute);

  public router: Router = inject(Router);

  public _unreadTransactions = inject(UnreadTransactionsService);

  public navigationGroups = navigationGroups;

  public backendUrl: string = environment.url;

  public companyData: CompanyV2Dto = this._token.getUser().company;

  public isProdVersion: boolean = environment.production;

  public disableNavigation: boolean = false;

  /** Viewport below the mobile breakpoint → drawer in 'over' mode */
  public isMobile = signal(window.innerWidth < MOBILE_BREAKPOINT);

  /** Drawer open state (bound to mat-drawer) */
  public sidebarOpened = signal(window.innerWidth >= MOBILE_BREAKPOINT);

  /** Current router url, updated on navigation */
  private currentUrl = signal('');

  /** Number of transactions pending validation (sidebar badge + topbar dot) */
  public unreadCount = signal(0);

  /** Active navigation item derived from the current url */
  public activeItem = computed<NavigationButton | undefined>(() => {
    const url = this.currentUrl();
    return (
      navigationButtons.find(
        (item) => item.location !== '' && url.includes(item.location)
      ) ?? navigationButtons.find((item) => item.location === '')
    );
  });

  private routerSubscription: Subscription;

  private unreadSubscription: Subscription;

  ngOnInit(): void {
    // Check if navigation should be disabled based on route data
    this.disableNavigation =
      this._route.snapshot.data['disableNavigation'] || false;

    this.currentUrl.set(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl.set(event.urlAfterRedirects);
      });

    // Other views (transactions, customer loyalty) update the count too
    this.unreadSubscription =
      this._unreadTransactions.unreadTransactionsCount.subscribe((count) => {
        this.unreadCount.set(Number(count) || 0);
      });

    this.getTransactions();
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.unreadSubscription?.unsubscribe();
  }

  /** Refresh the count of transactions pending validation */
  public getTransactions() {
    this._transactions
      .getUserTransactions(0, 10000, undefined, 'VALIDATION_PENDING')
      .subscribe({
        next: (value) => {
          this._unreadTransactions.setUnreadTransactionsCount(
            value.content.length
          );
        },
      });
  }

  /** Navigate to a sidebar item and close the drawer on mobile */
  public onNavItemClick(item: NavigationButton) {
    if (this.disableNavigation || item.location == null) return;

    this.router.navigate([item.location]);
    this.getTransactions();

    if (this.isMobile()) this.sidebarOpened.set(false);
  }

  /** User's first name for the sidebar footer */
  public getUserFirstName(): string {
    return this._token.getUser().firstName || '';
  }

  /** User's initials for the footer avatar */
  public getUserInitials(): string {
    const user = this._token.getUser();
    const initials =
      (user.firstName?.charAt(0) || '') + (user.lastName?.charAt(0) || '');
    return (
      initials.toUpperCase() || (user.email?.charAt(0) || '?').toUpperCase()
    );
  }

  /** Transloco key of the user's role label */
  public getUserRoleKey(): string {
    return this._token.getUser()?.role?.code === 'ROLE_ADMINISTRATOR'
      ? 'layout.roles.administrator'
      : 'layout.roles.user';
  }

  /** Logout from application, behind a confirmation */
  public logout() {
    this._layout.confirmLogout();
  }

  public toggleSidebar() {
    this.sidebarOpened.update((opened) => !opened);
  }

  /** Open the coupon-validation guide (help) in a new tab */
  public openHelp() {
    window.open('/#/coupon-validation', '_blank');
  }

  /** Keep drawer mode/state in sync with the viewport */
  @HostListener('window:resize') onResize() {
    const mobile = window.innerWidth < MOBILE_BREAKPOINT;
    if (mobile === this.isMobile()) return;

    this.isMobile.set(mobile);
    this.sidebarOpened.set(!mobile);
  }
}
